/**
 * Toque (docs/spec/mobile.md): o que no mouse é duplo clique e botão direito, no dedo é tocar e
 * segurar.
 *
 * - `isTouch(e)`: o gesto veio de um dedo? Usa o `pointerType` do evento; quando ele não vem (o
 *   Safari nem sempre o põe no `click`, e o teclado não tem), vale o do último `pointerdown`.
 * - `useLongPress()`: segurar o dedo abre o menu (o iPhone não gera `contextmenu`). O `bind` de cada
 *   item devolve os handlers, inclusive o `onContextMenu`, que segue valendo para o botão direito.
 *   O Android gera os dois (o segurar e o `contextmenu`): o menu abre uma vez só.
 */
import { useMemo, useRef, type MouseEvent, type PointerEvent } from 'react';
import type { MenuAnchor } from './ContextMenu.js';

/** Quanto tempo segurar o dedo para abrir o menu. */
export const LONG_PRESS_MS = 500;
/** Andar mais do que isto (px) é arrastar ou rolar, não segurar. */
export const LONG_PRESS_SLOP = 8;
/** Depois de abrir pelo segurar, o `contextmenu` e o clique que vêm junto são ignorados. */
const ECHO_MS = 800;

let lastPointerType = 'mouse';
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pointerdown', (e) => (lastPointerType = e.pointerType || lastPointerType), { capture: true, passive: true });
}

/** O ponteiro principal do aparelho é o dedo (celular, tablet)? */
export function coarsePointer(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}

/** O gesto veio de um dedo? */
export function isTouch(e?: { nativeEvent?: Event } | Event | null): boolean {
  const native = e && 'nativeEvent' in e ? e.nativeEvent : (e as Event | null | undefined);
  const type = (native as { pointerType?: string } | undefined)?.pointerType;
  return (type || lastPointerType) === 'touch';
}

/** O que os handlers do segurar precisam saber (sem React, para os testes). */
export type PressEvent = { pointerType: string; isPrimary: boolean; clientX: number; clientY: number; button: number };

/**
 * O segurar, sem React: um por componente (um dedo de cada vez), com o tempo e o relógio
 * injetáveis para os testes.
 */
export function createLongPress(opts: { ms?: number; slop?: number; now?: () => number; setTimer?: (fn: () => void, ms: number) => number; clearTimer?: (id: number) => void } = {}) {
  const ms = opts.ms ?? LONG_PRESS_MS;
  const slop = opts.slop ?? LONG_PRESS_SLOP;
  const now = opts.now ?? (() => Date.now());
  const setTimer = opts.setTimer ?? ((fn, wait) => window.setTimeout(fn, wait));
  const clearTimer = opts.clearTimer ?? ((id) => window.clearTimeout(id));
  let pending: { timer: number; x: number; y: number } | null = null;
  /** quando o menu abriu pelo dedo (o eco só existe no toque) */
  let firedAt = -Infinity;

  const cancel = () => {
    if (pending) clearTimer(pending.timer);
    pending = null;
  };
  const fire = (onPress: (at: MenuAnchor) => void, at: MenuAnchor, touch: boolean) => {
    if (touch) firedAt = now();
    onPress(at);
  };
  return {
    down(e: PressEvent, onPress: (at: MenuAnchor) => void): void {
      cancel();
      // o dedo desceu de novo: o eco do segurar anterior (que vem antes disso) já passou
      firedAt = -Infinity;
      if (e.pointerType !== 'touch' || !e.isPrimary || e.button > 0) return;
      const at = { x: e.clientX, y: e.clientY };
      pending = {
        x: e.clientX,
        y: e.clientY,
        timer: setTimer(() => {
          pending = null;
          fire(onPress, at, true);
        }, ms),
      };
    },
    move(e: Pick<PressEvent, 'clientX' | 'clientY'>): void {
      if (pending && Math.hypot(e.clientX - pending.x, e.clientY - pending.y) > slop) cancel();
    },
    up: cancel,
    /** O `contextmenu` (botão direito, ou o do Android ao segurar): abre, se o segurar ainda não abriu. */
    contextMenu(at: MenuAnchor, onPress: (at: MenuAnchor) => void, touch: boolean): void {
      cancel();
      if (touch && now() - firedAt < ECHO_MS) return;
      fire(onPress, at, touch);
    },
    /** O clique que segue um segurar não conta (não abre a ficha que acabou de mostrar o menu). */
    swallowClick(): boolean {
      return now() - firedAt < ECHO_MS;
    },
  };
}

export type LongPressHandlers = {
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onContextMenu: (e: MouseEvent) => void;
  onClickCapture: (e: MouseEvent) => void;
};

/**
 * Segurar o dedo (ou o botão direito) abre o menu. Um por componente; `bind(onPress)` para cada
 * item. Os handlers param o `contextmenu` ali (o do fundo, de quem está por fora, não abre junto).
 */
export function useLongPress(ms = LONG_PRESS_MS): { bind: (onPress: (at: MenuAnchor) => void) => LongPressHandlers } {
  const press = useRef<ReturnType<typeof createLongPress> | null>(null);
  press.current ??= createLongPress({ ms });
  return useMemo(
    () => ({
      bind: (onPress) => ({
        onPointerDown: (e) => press.current!.down(e, onPress),
        onPointerMove: (e) => press.current!.move(e),
        onPointerUp: () => press.current!.up(),
        onPointerCancel: () => press.current!.up(),
        onContextMenu: (e) => {
          e.preventDefault();
          e.stopPropagation();
          press.current!.contextMenu({ x: e.clientX, y: e.clientY }, onPress, isTouch(e));
        },
        onClickCapture: (e) => {
          if (!press.current!.swallowClick()) return;
          e.preventDefault();
          e.stopPropagation();
        },
      }),
    }),
    [],
  );
}
